import { useEffect, useState } from "react";

import { FRAME_TIMEOUT_MS, PREVIEW_KIND, VIEWER_STATUS } from "../misc/constants";

/**
 * The browser's own reader is the only reader, so whether it will render a PDF
 * inline decides whether there is anything to show at all. `pdfViewerEnabled`
 * is the one honest answer to that; where it is not implemented the frame is
 * attempted anyway, and the deadline below covers a frame that comes up blank.
 */
const canRenderPdfNatively = () => window.navigator?.pdfViewerEnabled !== false;

/**
 * Drives the embedded contract viewer and reports whether the recipient has
 * actually been shown the document, which is what gates the acknowledgement.
 *
 * The document is handed to the browser's own PDF reader in an iframe, as
 * the blob URL use-contract-preview loaded from the /document route. Nothing
 * inside the reader is observable, so the strongest evidence available is
 * that the frame loaded, and the download stands behind it for everything
 * else.
 *
 * `active` is the modal's open state: the frame only exists while the modal is
 * mounted, so re-opening the same contract has to load it again.
 * @param options
 */
const usePdfViewer = ({ url, kind, active }) => {
    const [status, setStatus] = useState(VIEWER_STATUS.IDLE);
    // Whether the frame reported the document as displayed.
    const [frameReady, setFrameReady] = useState(false);
    // Bumped on an explicit retry. Doubles as the frame's key, so retrying a
    // document at the same URL actually reloads it instead of leaving React
    // to conclude nothing changed.
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        setFrameReady(false);

        if (!active || !url || kind !== PREVIEW_KIND.PDF) {
            setStatus(VIEWER_STATUS.IDLE);
            return;
        }

        setStatus(canRenderPdfNatively() ? VIEWER_STATUS.FRAME : VIEWER_STATUS.ERROR);
    }, [url, kind, active, attempt]);

    // A frame that never loads would sit there blank forever, and a blank
    // contract the recipient believes they have read is the worst outcome
    // available — so the frame is given a deadline, then gives way to the
    // download.
    useEffect(() => {
        if (status !== VIEWER_STATUS.FRAME || frameReady) return undefined;

        const timer = window.setTimeout(() => setStatus(VIEWER_STATUS.ERROR), FRAME_TIMEOUT_MS);

        return () => window.clearTimeout(timer);
    }, [status, frameReady, attempt]);

    return {
        status,
        frameReady,
        frameKey    : attempt,
        onFrameReady: () => setFrameReady(true),
        retry       : () => setAttempt((value) => value + 1)
    };
};

export default usePdfViewer;
